import * as React from 'react';
import type { InjectedRouter } from 'react-router';

import { StatsigProvider } from '@coursera-web/statsig';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import connectToStores from 'js/lib/connectToStores';

import PageFooter from 'bundles/page-footer/components/PageFooter';
import PremiumHubPageHeader from 'bundles/premium-hub/components/shared/PremiumHubPageHeader';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';
import InteractionGuard from 'bundles/unified-common/components/InteractionGuard';
import GlobalCoachChat from 'bundles/unified-common/components/coach-on-discovery/GlobalCoachChat';
import {
  GlobalCoachChatStateProvider,
  useGlobalCoachChatState,
} from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';
import NavAiSparkleExperimentProvider, {
  useNavAiSparkle,
} from 'bundles/unified-common/providers/NavAiSparkleExperimentProvider';

import 'css!../styl/index';

import { useIsOnDegreesPage } from '../hooks/useIsOnDegreesPage';

type Context = {
  router: InjectedRouter;
  getStore: (storeName: string) => void;
};

type Props = {
  children?: React.ReactNode;
  requestCountryCode?: string;
  csrfToken?: string;
} & Context;

const PremiumHubApp = ({ children }: Props) => {
  const onDegreesPage = useIsOnDegreesPage();
  const { showNavAiSparkle } = useNavAiSparkle();
  const { isContextProvided, coachOpen, pageChatType } = useGlobalCoachChatState();
  const isFullscreenCoachChatOpen = isContextProvided && coachOpen && pageChatType === null;

  return (
    <div className="rc-PremiumHubApp">
      {!onDegreesPage && <PremiumHubPageHeader isSimplified={false} />}
      {showNavAiSparkle && <GlobalCoachChat />}
      <InteractionGuard preventInteraction={isFullscreenCoachChatOpen}>
        {children}
        <PageFooter />
      </InteractionGuard>
    </div>
  );
};

const PremiumHubAppWithProvider = ({ children, ...props }: Props) => {
  const onDegreesPage = useIsOnDegreesPage();

  let content: React.ReactNode;
  if (onDegreesPage) {
    content = <PremiumHubApp {...props}>{children}</PremiumHubApp>;
  } else {
    content = (
      <GlobalCoachChatStateProvider>
        <NavAiSparkleExperimentProvider>
          <PremiumHubApp {...props}>{children}</PremiumHubApp>
        </NavAiSparkleExperimentProvider>
      </GlobalCoachChatStateProvider>
    );
  }
  return <StatsigProvider>{content}</StatsigProvider>;
};

export default compose<Props, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'program_hub',
      page: 'premium_hub_app',
    },
  })),
  connectToStores<Props, Props & { csrfToken: string; requestCountryCode: string }>(
    [ApplicationStoreClass],
    (ApplicationStore, props) => ({
      ...props,
      csrfToken: ApplicationStore.getState().csrfToken,
      requestCountryCode: ApplicationStore.getState().requestCountryCode,
    })
  )
)(PremiumHubAppWithProvider);
